import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import { SectionCard, EmptyState } from '../ui';
import { paymentService, type BonusTier, type PricingRow } from '../../services/paymentService';

interface Props {
  pricing: PricingRow[];
  tiers: BonusTier[];
  rateBasisDays: number;
  onChanged: () => void;
  onToast: (msg: string, sev: 'success' | 'error') => void;
}

const num = (v: string): number => Number(String(v).replace(',', '.'));

export const PricingEditor: React.FC<Props> = ({ pricing, tiers, rateBasisDays, onChanged, onToast }) => {
  const [busy, setBusy] = useState(false);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [newPrice, setNewPrice] = useState({ licenseType: '', durationDays: '', pricePerDevice: '' });
  const [newTier, setNewTier] = useState({ minAmountUsdt: '', bonusPercent: '' });

  const rowKey = (r: PricingRow) => `${r.licenseType}|${r.durationDays}`;

  /**
   * Giá/ngày ở mốc chuẩn — con số thật sự quyết định phần bù khi khách nâng gói.
   * Hiện nó ra cạnh bảng giá vì nếu không, việc sửa dòng 30 ngày trông như chỉ
   * đổi giá một gói, trong khi nó đổi luôn công thức quy đổi của mọi lần nâng gói.
   */
  const dailyRates = useMemo(() => {
    const out: Array<{ type: string; rate: number }> = [];
    for (const p of pricing) {
      if (p.durationDays === rateBasisDays && p.isActive) {
        out.push({ type: p.licenseType, rate: p.pricePerDevice / rateBasisDays });
      }
    }
    return out.sort((a, b) => a.rate - b.rate);
  }, [pricing, rateBasisDays]);

  const savePrice = async (row: PricingRow, patch: Partial<PricingRow>) => {
    setBusy(true);
    try {
      const res = await paymentService.setPrice({
        licenseType: row.licenseType,
        durationDays: row.durationDays,
        pricePerDevice: patch.pricePerDevice ?? row.pricePerDevice,
        isActive: patch.isActive ?? row.isActive,
      });
      onToast(
        res.affectsUpgradeRate
          ? `Đã lưu. Đây là mốc ${rateBasisDays} ngày nên giá quy đổi khi nâng gói cũng đổi theo.`
          : 'Đã lưu bảng giá.',
        'success',
      );
      setDraft((d) => {
        const next = { ...d };
        delete next[rowKey(row)];
        return next;
      });
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addPrice = async () => {
    const licenseType = newPrice.licenseType.trim().toUpperCase();
    const durationDays = num(newPrice.durationDays);
    const pricePerDevice = num(newPrice.pricePerDevice);
    if (!licenseType || !Number.isInteger(durationDays) || durationDays <= 0 || !Number.isFinite(pricePerDevice)) {
      onToast('Nhập đủ gói, số ngày (nguyên dương) và giá.', 'error');
      return;
    }
    setBusy(true);
    try {
      await paymentService.setPrice({ licenseType, durationDays, pricePerDevice, isActive: true });
      setNewPrice({ licenseType: '', durationDays: '', pricePerDevice: '' });
      onToast('Đã thêm dòng giá.', 'success');
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const saveTier = async (tier: BonusTier, patch: Partial<BonusTier>) => {
    setBusy(true);
    try {
      await paymentService.setTier({
        minAmountUsdt: tier.minAmountUsdt,
        bonusPercent: patch.bonusPercent ?? tier.bonusPercent,
        isActive: patch.isActive ?? tier.isActive,
      });
      onToast('Đã lưu bậc bonus.', 'success');
      setDraft((d) => {
        const next = { ...d };
        delete next[`tier|${tier.minAmountUsdt}`];
        return next;
      });
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addTier = async () => {
    const minAmountUsdt = num(newTier.minAmountUsdt);
    const bonusPercent = num(newTier.bonusPercent);
    if (!Number.isFinite(minAmountUsdt) || !Number.isFinite(bonusPercent)) {
      onToast('Nhập đủ mốc tiền và % bonus.', 'error');
      return;
    }
    setBusy(true);
    try {
      await paymentService.setTier({ minAmountUsdt, bonusPercent, isActive: true });
      setNewTier({ minAmountUsdt: '', bonusPercent: '' });
      onToast('Đã thêm bậc bonus.', 'success');
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const removeTier = async (minAmountUsdt: number) => {
    setBusy(true);
    try {
      await paymentService.removeTier(minAmountUsdt);
      onToast('Đã xoá bậc bonus.', 'success');
      onChanged();
    } catch (e) {
      onToast((e as Error).message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const hasBaseTier = tiers.some((t) => t.minAmountUsdt === 0 && t.isActive);

  return (
    <Stack spacing={3}>
      <SectionCard
        title="Bảng giá license"
        description={`Giá tính theo MỖI THIẾT BỊ. Mốc ${rateBasisDays} ngày còn là chuẩn quy đổi giá/ngày khi khách đổi sang gói đắt hơn.`}
        flush
      >
        {dailyRates.length > 0 && (
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Alert severity="info">
              <Typography variant="body2">
                Giá mỗi ngày ở mốc {rateBasisDays} ngày:{' '}
                {dailyRates.map((r, i) => (
                  <React.Fragment key={r.type}>
                    {i > 0 && ' · '}
                    <b>{r.type.replace(/_/g, ' ')}</b> {r.rate.toFixed(4)}
                  </React.Fragment>
                ))}
                . Nâng gói thu phần chênh của hai con số này nhân số ngày còn lại.
              </Typography>
            </Alert>
          </Box>
        )}

        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Gói</TableCell>
                <TableCell align="right">Số ngày</TableCell>
                <TableCell align="right">Giá / thiết bị</TableCell>
                <TableCell align="center">Đang bán</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {pricing.map((r) => {
                const k = rowKey(r);
                const dirty = draft[k] !== undefined && num(draft[k]) !== r.pricePerDevice;
                return (
                  <TableRow key={k} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{r.licenseType.replace(/_/g, ' ')}</TableCell>
                    <TableCell align="right">
                      {r.durationDays}
                      {r.durationDays === rateBasisDays && (
                        <Tooltip title="Mốc chuẩn quy đổi nâng gói">
                          <Box component="span" sx={{ ml: 0.5, color: 'warning.main' }}>★</Box>
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 140 }}>
                      <TextField
                        size="small"
                        value={draft[k] ?? String(r.pricePerDevice)}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={r.isActive}
                        disabled={busy}
                        onChange={(e) => void savePrice(r, { isActive: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<SaveOutlinedIcon />}
                        disabled={busy || !dirty}
                        onClick={() => void savePrice(r, { pricePerDevice: num(draft[k]) })}
                      >
                        Lưu
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
              {pricing.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5}>
                    <EmptyState title="Chưa có dòng giá nào" dense />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2.5 }} alignItems="flex-start">
          <TextField
            size="small"
            label="Gói"
            placeholder="PROMAX"
            value={newPrice.licenseType}
            onChange={(e) => setNewPrice((p) => ({ ...p, licenseType: e.target.value }))}
          />
          <TextField
            size="small"
            label="Số ngày"
            placeholder="30"
            value={newPrice.durationDays}
            onChange={(e) => setNewPrice((p) => ({ ...p, durationDays: e.target.value }))}
          />
          <TextField
            size="small"
            label="Giá / thiết bị"
            placeholder="19"
            value={newPrice.pricePerDevice}
            onChange={(e) => setNewPrice((p) => ({ ...p, pricePerDevice: e.target.value }))}
          />
          <Button startIcon={<AddRoundedIcon />} onClick={() => void addPrice()} disabled={busy}>
            Thêm dòng
          </Button>
        </Stack>
      </SectionCard>

      <SectionCard
        title="Bậc bonus nạp điểm"
        description="Bậc khớp là mốc LỚN NHẤT không vượt quá số tiền thực nhận. Bonus tính lại theo số USDT thật, không theo số khách chọn lúc tạo đơn."
        flush
      >
        {!hasBaseTier && (
          <Box sx={{ px: 2.5, pt: 2 }}>
            <Alert severity="warning">
              Thiếu bậc đáy <b>0</b> đang bật. Khoản nhỏ hơn mốc thấp nhất sẽ lặng lẽ về 0% —
              thêm một bậc mốc 0 để chủ động khai báo mức đó.
            </Alert>
          </Box>
        )}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell align="right">Từ (USDT)</TableCell>
                <TableCell align="right">Bonus %</TableCell>
                <TableCell align="center">Đang bật</TableCell>
                <TableCell align="right" />
              </TableRow>
            </TableHead>
            <TableBody>
              {tiers.map((t) => {
                const k = `tier|${t.minAmountUsdt}`;
                const dirty = draft[k] !== undefined && num(draft[k]) !== t.bonusPercent;
                return (
                  <TableRow key={k} hover>
                    <TableCell align="right" sx={{ fontWeight: 600 }}>
                      {t.minAmountUsdt}
                    </TableCell>
                    <TableCell align="right" sx={{ width: 120 }}>
                      <TextField
                        size="small"
                        value={draft[k] ?? String(t.bonusPercent)}
                        onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                        inputProps={{ style: { textAlign: 'right' } }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Switch
                        size="small"
                        checked={t.isActive}
                        disabled={busy}
                        onChange={(e) => void saveTier(t, { isActive: e.target.checked })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<SaveOutlinedIcon />}
                        disabled={busy || !dirty}
                        onClick={() => void saveTier(t, { bonusPercent: num(draft[k]) })}
                      >
                        Lưu
                      </Button>
                      <Tooltip title={t.minAmountUsdt === 0 ? 'Không xoá được bậc đáy 0' : 'Xoá bậc'}>
                        <span>
                          <IconButton
                            size="small"
                            color="error"
                            disabled={busy || t.minAmountUsdt === 0}
                            onClick={() => void removeTier(t.minAmountUsdt)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })}
              {tiers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4}>
                    <EmptyState title="Chưa có bậc bonus nào" dense />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} sx={{ p: 2.5 }} alignItems="flex-start">
          <TextField
            size="small"
            label="Từ (USDT)"
            placeholder="200"
            value={newTier.minAmountUsdt}
            onChange={(e) => setNewTier((t) => ({ ...t, minAmountUsdt: e.target.value }))}
          />
          <TextField
            size="small"
            label="Bonus %"
            placeholder="60"
            value={newTier.bonusPercent}
            onChange={(e) => setNewTier((t) => ({ ...t, bonusPercent: e.target.value }))}
          />
          <Button startIcon={<AddRoundedIcon />} onClick={() => void addTier()} disabled={busy}>
            Thêm bậc
          </Button>
        </Stack>
      </SectionCard>
    </Stack>
  );
};
